import { executeNavigation } from './voiceQuickNav';

/** When true, voice navigation away from `/dashboard` collapses the desktop rail to FAB. */
export const COLLAPSE_RAIL_ON_VOICE_NAVIGATE = true;

export const DASHBOARD_REFRESH_PATTERN =
  /(?:refresh|reload|update)\s+(?:the\s+)?(?:dashboard(?:\s+data)?|home(?:\s+page)?|financial\s+(?:data|metrics|summary))/i;

const DASHBOARD_MUTATION_ACTIONS = new Set([
  'create_expense',
  'record_expense',
  'record_travel_expense',
  'record_office_expense',
  'update_expense',
  'batch_create_expenses',
  'create_sales_invoice',
  'create_purchase_invoice',
  'create_bill',
  'update_invoice',
  'void_invoice',
  'record_payment',
  'create_journal_entry',
  'update_journal_entry',
  'delete_journal_entry',
]);

export const isDashboardPath = (pathname: string): boolean =>
  pathname === '/dashboard' || pathname.startsWith('/dashboard/');

export const routePathname = (route: string): string => route.split('?')[0] || route;

export const shouldRefreshDashboardAfterMutation = (action?: string): boolean =>
  Boolean(action && DASHBOARD_MUTATION_ACTIONS.has(action));

export const maybeCollapseRailOnVoiceNavigate = (
  destination: string,
  setRailOpen: (open: boolean) => void
): void => {
  if (!COLLAPSE_RAIL_ON_VOICE_NAVIGATE) return;
  if (!isDashboardPath(window.location.pathname)) return;

  const targetPath = routePathname(destination);
  if (isDashboardPath(targetPath)) return;

  setRailOpen(false);
};

/** Full dashboard refresh when a financial mutation completes while the user is on the dashboard. */
export const maybeRefreshDashboardAfterMutation = (action?: string): void => {
  if (!shouldRefreshDashboardAfterMutation(action)) return;
  if (!isDashboardPath(window.location.pathname)) return;
  dispatchVoiceDashboardAction({ refresh: true });
};

export interface DashboardRefreshFastTrackResult {
  handled: true;
  message: string;
}

export const dispatchVoiceDashboardAction = (detail: Record<string, unknown>): void => {
  const onDashboard = window.location.pathname === '/dashboard';
  if (!onDashboard && detail.refresh) {
    executeNavigation('/dashboard');
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('voiceDashboardAction', { detail }));
    }, 300);
    return;
  }

  window.dispatchEvent(new CustomEvent('voiceDashboardAction', { detail }));
};

export const tryDashboardRefreshFastTrack = (text: string): DashboardRefreshFastTrackResult | null => {
  const trimmed = text.trim();
  if (!DASHBOARD_REFRESH_PATTERN.test(trimmed)) return null;

  const onDashboard = window.location.pathname === '/dashboard';
  dispatchVoiceDashboardAction({ refresh: true });
  return {
    handled: true,
    message: onDashboard ? 'Refreshing dashboard data.' : 'Opening dashboard and refreshing data.',
  };
};

const PENDING_REVIEWS_PATTERN =
  /\b(?:show\s+pending\s+(?:document\s+)?reviews?|pending\s+(?:document\s+)?reviews?)\b/i;

const TRIAL_BALANCE_PATTERN = /\b(?:generate|show|view)\s+(?:the\s+)?trial\s+balance\b/i;

const ANALYZE_EXPENSES_PATTERN = /\banalyze\s+(?:q[1-4]\s+)?expenses?\b/i;

const PREDICT_CASH_FLOW_PATTERN = /\b(?:predict|forecast)\s+cash\s+flow\b/i;

export const resolveQuarterDateRange = (
  text: string
): { dateFrom: string; dateTo: string } | null => {
  const match = text.match(/\bq([1-4])\b/i);
  if (!match) return null;

  const year = new Date().getFullYear();
  const quarter = Number(match[1]);
  const startMonth = (quarter - 1) * 3;
  const dateFrom = `${year}-${String(startMonth + 1).padStart(2, '0')}-01`;
  const endMonth = startMonth + 3;
  const lastDay = new Date(year, endMonth, 0).getDate();
  const dateTo = `${year}-${String(endMonth).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { dateFrom, dateTo };
};

const buildExpensesRoute = (dateFrom?: string, dateTo?: string): string => {
  const params = new URLSearchParams();
  if (dateFrom) params.set('date_from', dateFrom);
  if (dateTo) params.set('date_to', dateTo);
  const qs = params.toString();
  return `/expenses${qs ? `?${qs}` : ''}`;
};

const buildReportsRoute = (reportType: string): string => {
  const params = new URLSearchParams({ tab: 'financial', type: reportType, generate: '1' });
  return `/reports?${params.toString()}`;
};

const navigateWithRailCollapse = (
  destination: string,
  setRailOpen?: (open: boolean) => void
): void => {
  if (setRailOpen) maybeCollapseRailOnVoiceNavigate(destination, setRailOpen);
  executeNavigation(destination);
};

export const tryDashboardSuggestedQueryFastTrack = (
  text: string,
  setRailOpen?: (open: boolean) => void
): DashboardRefreshFastTrackResult | null => {
  const trimmed = text.trim();

  if (PENDING_REVIEWS_PATTERN.test(trimmed)) {
    navigateWithRailCollapse('/image-processing', setRailOpen);
    return { handled: true, message: 'Opening pending document reviews.' };
  }

  if (ANALYZE_EXPENSES_PATTERN.test(trimmed)) {
    const quarterRange = resolveQuarterDateRange(trimmed);
    const destination = buildExpensesRoute(quarterRange?.dateFrom, quarterRange?.dateTo);
    navigateWithRailCollapse(destination, setRailOpen);
    if (quarterRange) {
      window.dispatchEvent(
        new CustomEvent('voiceExpensesAction', {
          detail: { date_from: quarterRange.dateFrom, date_to: quarterRange.dateTo },
        })
      );
    }
    const quarterLabel = trimmed.match(/\bq[1-4]\b/i)?.[0]?.toUpperCase();
    return {
      handled: true,
      message: quarterLabel
        ? `Opening expenses filtered to ${quarterLabel}.`
        : 'Opening expenses for analysis.',
    };
  }

  if (TRIAL_BALANCE_PATTERN.test(trimmed)) {
    const destination = buildReportsRoute('trial-balance');
    navigateWithRailCollapse(destination, setRailOpen);
    window.dispatchEvent(
      new CustomEvent('voiceReportsAction', {
        detail: { tab: 'financial', reportType: 'trial-balance', generate: true },
      })
    );
    return { handled: true, message: 'Generating trial balance report.' };
  }

  if (PREDICT_CASH_FLOW_PATTERN.test(trimmed)) {
    const destination = buildReportsRoute('cash-flow');
    navigateWithRailCollapse(destination, setRailOpen);
    window.dispatchEvent(
      new CustomEvent('voiceReportsAction', {
        detail: { tab: 'financial', reportType: 'cash-flow', generate: true },
      })
    );
    return { handled: true, message: 'Generating cash flow report.' };
  }

  return null;
};
